import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown>[];
}

export default function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = meta.title;

    const setMetaTag = (name: string, content: string, propName = 'name') => {
      let el = document.querySelector(`meta[${propName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(propName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const removeMetaTag = (name: string, propName = 'name') => {
      const el = document.querySelector(`meta[${propName}="${name}"]`);
      if (el) el.remove();
    };

    if (meta.description) setMetaTag('description', meta.description);
    if (meta.keywords) setMetaTag('keywords', meta.keywords);

    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) existingCanonical.remove();
    if (meta.canonicalUrl) {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = meta.canonicalUrl;
      document.head.appendChild(link);
    }

    if (meta.ogTitle) setMetaTag('og:title', meta.ogTitle, 'property');
    if (meta.ogDescription) setMetaTag('og:description', meta.ogDescription, 'property');
    if (meta.ogType) setMetaTag('og:type', meta.ogType, 'property');
    if (meta.ogUrl) setMetaTag('og:url', meta.ogUrl, 'property');
    if (meta.ogImage) setMetaTag('og:image', meta.ogImage, 'property');

    const jsonLdScripts: HTMLScriptElement[] = [];
    if (meta.jsonLd) {
      meta.jsonLd.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        jsonLdScripts.push(script);
      });
    }

    return () => {
      document.title = prevTitle;
      removeMetaTag('description');
      removeMetaTag('keywords');
      removeMetaTag('og:title', 'property');
      removeMetaTag('og:description', 'property');
      removeMetaTag('og:type', 'property');
      removeMetaTag('og:url', 'property');
      removeMetaTag('og:image', 'property');
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.remove();
      jsonLdScripts.forEach((s) => s.remove());
    };
  }, [meta]);
}